/**
 * Detalhe de evento de plantão no drill-down da auditoria — extraído de
 * AuditRow.tsx (GUPM-335, limite de 200 linhas por componente).
 */
import { actorDisplay, type AuditEntry } from '@/domain/auditoria';
import type { HandoverAuditView } from '@/domain/auditoria/handover';
import {
  countDivergencesFromResponses,
  itemHasDivergence,
} from '@/domain/shift-handover/checklist-rules';
import type { ChecklistItem, ChecklistResponse } from '@/domain/shift-handover/types';

/**
 * Detalhe de um evento de plantão (GUPM-273).
 *
 * Só exibe o que pertence a ESTE momento: o payload é o snapshot da linha
 * inteira, então um encerramento carrega também o checklist e a observação da
 * abertura. Exibi-los aqui atribuiria a quem encerrou o que quem abriu escreveu.
 *
 * Quando não há checklist a exibir, a ausência é a informação — é ela que
 * separa, no registro, um encerramento atestado de um destrave administrativo.
 */
export function HandoverDetail({
  view,
  entry,
  catalogById,
}: {
  view: HandoverAuditView;
  entry: AuditEntry;
  catalogById: Map<string, ChecklistItem>;
}) {
  const nada = view.textos.length === 0 && view.checklist.length === 0;
  return (
    <div className="space-y-4">
      {view.textos.map((t) => (
        <dl key={t.label} className="rounded-md border border-jf-border bg-jf-surface p-2 text-xs">
          <dt className="font-bold text-jf-dim">{t.label}</dt>
          <dd className="text-white break-words mt-0.5 whitespace-pre-wrap">{t.value}</dd>
        </dl>
      ))}

      {view.checklistLabel && (
        <div className="space-y-2">
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wide text-jf-dim">
              {view.checklistLabel}
            </h4>
            <p className="text-[10px] text-jf-muted">por {actorDisplay(entry)}</p>
          </div>
          <ChecklistBlock responses={view.checklist} catalogById={catalogById} />
        </div>
      )}

      {nada && (
        <p className="text-xs text-jf-muted italic">
          Sem checklist nem comentário neste evento.
        </p>
      )}
    </div>
  );
}

function ChecklistBlock({
  responses,
  catalogById,
}: {
  responses: ChecklistResponse[];
  catalogById: Map<string, ChecklistItem>;
}) {
  const divergencias = countDivergencesFromResponses(responses, catalogById);
  return (
    <div className="space-y-3">
      {/* Resumo de status — paridade com a antiga /historico-trocas, embutido no
          drill-down da auditoria (GUPM-147). */}
      <div className="flex items-center gap-2">
        {divergencias === 0 ? (
          <span className="rounded-full bg-green-500/15 text-green-300 px-2 py-0.5 text-[10px] font-bold uppercase">
            OK
          </span>
        ) : (
          <span className="rounded-full bg-orange-500/15 text-orange-300 px-2 py-0.5 text-[10px] font-bold uppercase">
            {divergencias} divergência{divergencias > 1 ? 's' : ''}
          </span>
        )}
        <span className="text-xs text-jf-muted">
          {responses.length} {responses.length === 1 ? 'item respondido' : 'itens respondidos'}
        </span>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {responses
          .map((resp) => ({ resp, item: catalogById.get(resp.item_id) }))
          .sort((a, b) => (a.item?.order_index ?? 9999) - (b.item?.order_index ?? 9999))
          .map(({ resp, item }) => {
            const diverge = !item || itemHasDivergence(item, resp);
            return (
              <div
                key={resp.item_id}
                className={`rounded-md border p-2 text-xs ${
                  diverge ? 'border-orange-500/50 bg-orange-500/5' : 'border-jf-border bg-jf-surface'
                }`}
              >
                <span className="font-bold text-jf-dim mr-1">{item?.order_index ?? '?'}.</span>
                <span className="text-white">{item?.label ?? '(item removido do catálogo)'}</span>
                <span
                  className={`ml-2 inline-block rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${
                    resp.answer === 'sim' ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'
                  }`}
                >
                  {resp.answer ?? '—'}
                </span>
              </div>
            );
          })}
      </div>
    </div>
  );
}
